<script setup>
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGarageStore } from '@/stores/garage.js'

import CheckCircle from '@/assets/icons/check_circle_24dp_FILL1_wght400_GRAD0_opsz24.svg'

const props = defineProps({
  step: {
    type: Number,
    required: true
  },
  icon: {
    type: String,
    required: true
  },
  type: {
    type: String,
    required: true
  },
  value: {
    type: String,
    required: true
  },
  hasValue: {
    type: Boolean,
    default: false
  },
  background: {
    type: String,
    required: false
  }
})

// I18n
const { t } = useI18n()

// Stores
const garageStore = useGarageStore()

// Variables

// Event Handlers
onMounted(async () => {
})

watch(() => null, () => {
})

// Functions
function setCardClasses() {
  if (props.step === 0) {
    return 'bg-white shadow-xl current-step'
  } else if (props.step < 0) {
    return 'bg-gray-100 inactive-step complete'
  } else {
    return 'bg-gray-100 inactive-step'
  }
}

function setCardIcon() {
  let icon = props.icon;
  if (props.step < 0 || props.hasValue) {
    icon = CheckCircle
  }

  return icon;
}

</script>

<template>
  <div class="relative flex flex-col items-center w-full lg:flex-1 px-2 lg:px-1">
    <div class="relative w-full h-24 lg:h-48 rounded-lg flex flex-col items-center justify-center" :class="setCardClasses()" :style="{ backgroundImage: props.background ? `url(${props.background})` : '', backgroundSize: 'cover', backgroundPosition: 'center' }">
      <!-- Animated outline for current step -->
      <div v-if="props.step === 0" class="absolute inset-0 rounded-lg border-4 border-transparent animate-outline-fill"></div>
      
      <div class="w-full h-24 lg:h-48 flex flex-col items-center justify-center" style="background-color: rgba(255, 255, 255, 0.90)">
        <Transition name="icon-change" mode="out-in">
          <img :key="setCardIcon()" :src="setCardIcon()" class="h-[30px] lg:h-[60px]" alt="card icon"/>
        </Transition>
        <p class="text-xs text-gray-600 hidden lg:block">{{ props.type }}</p>
        <p class="text-center mt-1 lg:mt-2 text-sm lg:text-lg font-semibold">{{ props.value }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/request_a_quote/request_a_quote';

.animate-outline-fill {
  border-color: #b05b34;
  animation: outline-fill 0.8s ease-out forwards;
}

.icon-change-enter-active,
.icon-change-leave-active {
  transition: all 0.3s ease;
}

.icon-change-enter-from {
  opacity: 0;
  transform: scale(0.8) rotate(-10deg);
}

.icon-change-leave-to {
  opacity: 0;
  transform: scale(1.2) rotate(10deg);
}

.icon-change-enter-to {
  opacity: 1;
  transform: scale(1) rotate(0deg);
}

@keyframes outline-fill {
  0% {
    clip-path: polygon(50% 0, 50% 0, 50% 0, 50% 0);
  }
  5% {
    clip-path: polygon(40% 0, 60% 0, 60% 0, 40% 0);
  }
  10% {
    clip-path: polygon(30% 0, 70% 0, 70% 0, 30% 0);
  }
  15% {
    clip-path: polygon(20% 0, 80% 0, 80% 0, 20% 0);
  }
  20% {
    clip-path: polygon(10% 0, 90% 0, 90% 0, 10% 0);
  }
  25% {
    clip-path: polygon(0 0, 100% 0, 100% 0, 0 0);
  }
  30% {
    clip-path: polygon(0 0, 100% 0, 100% 10%, 0 10%);
  }
  35% {
    clip-path: polygon(0 0, 100% 0, 100% 20%, 0 20%);
  }
  40% {
    clip-path: polygon(0 0, 100% 0, 100% 30%, 0 30%);
  }
  45% {
    clip-path: polygon(0 0, 100% 0, 100% 40%, 0 40%);
  }
  50% {
    clip-path: polygon(0 0, 100% 0, 100% 50%, 0 50%);
  }
  55% {
    clip-path: polygon(0 0, 100% 0, 100% 60%, 0 60%);
  }
  60% {
    clip-path: polygon(0 0, 100% 0, 100% 70%, 0 70%);
  }
  65% {
    clip-path: polygon(0 0, 100% 0, 100% 80%, 0 80%);
  }
  70% {
    clip-path: polygon(0 0, 100% 0, 100% 90%, 0 90%);
  }
  75% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
  }
  100% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
  }
}
</style>
